import {
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Loader2,
  RotateCw,
  ShieldAlert,
  X,
} from "lucide-react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isTauri } from "../../lib/local-media.js";

/**
 * In-App-Browser — universelle Komponente zum Anzeigen externer URLs
 * **ohne** die App zu verlassen.
 *
 * Strategie pro Runtime:
 *
 * - **Web/PWA und Tauri**: Inhalt wird in einem sandboxed `<iframe>` im
 *   Glass-Sheet gerendert. Viele Seiten blockieren Embedding via
 *   `X-Frame-Options` / `CSP frame-ancestors` — dafür gibt es einen
 *   Fallback-Button („In neuem Tauri-Fenster öffnen" bzw. „Im Browser
 *   öffnen") sowie eine 4-Sekunden-Heuristik, die einen Block-Hinweis
 *   anzeigt, wenn das iframe nicht lädt.
 *
 * - **Tauri Desktop**: zusätzlich verfügbar ist die Aktion
 *   „In Tauri-Fenster öffnen" — diese spawned ein eigenes `WebviewWindow`
 *   im selben App-Prozess. Damit funktionieren auch Seiten, die das
 *   Iframe-Embedding verbieten, ohne dass der User in einen externen
 *   Browser wechseln muss.
 *
 * Architektur: React Context + `useInAppBrowser()` Hook. Aufrufer kennen
 * den Renderer nicht — sie rufen nur `open(url, opts)`.
 */

export interface OpenOptions {
  /** Anzeigetitel im Sheet-Header. */
  title?: string;
  /**
   * Wenn der User „extern öffnen" wählt: Standard-URL ist `url`, kann
   * hier aber überschrieben werden (z. B. wenn die App URL und External-
   * URL aus rechtlichen Gründen unterschiedlich sind).
   */
  externalUrl?: string;
}

interface InAppBrowserState {
  url: string;
  title: string;
  externalUrl: string;
}

interface InAppBrowserApi {
  open: (url: string, opts?: OpenOptions) => void;
  close: () => void;
}

const Ctx = createContext<InAppBrowserApi | null>(null);

/**
 * Hook für Konsumenten — von jedem Komponentenbaum unterhalb von
 * `<InAppBrowserProvider>` aufrufbar.
 */
export function useInAppBrowser(): InAppBrowserApi {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useInAppBrowser muss innerhalb <InAppBrowserProvider> verwendet werden");
  }
  return ctx;
}

export function InAppBrowserProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<InAppBrowserState | null>(null);

  const api = useMemo<InAppBrowserApi>(
    () => ({
      open: (url, opts) => {
        const safeUrl = normalizeUrl(url);
        if (!safeUrl) return;
        setState({
          url: safeUrl,
          title: opts?.title ?? hostnameOf(safeUrl) ?? safeUrl,
          externalUrl: opts?.externalUrl ?? safeUrl,
        });
      },
      close: () => setState(null),
    }),
    [],
  );

  return (
    <Ctx.Provider value={api}>
      {children}
      {state && <BrowserSheet state={state} onClose={api.close} />}
    </Ctx.Provider>
  );
}

/* ────────────────────────────── Sheet ────────────────────────────── */

function BrowserSheet({ state, onClose }: { state: InAppBrowserState; onClose: () => void }) {
  const [currentUrl, setCurrentUrl] = useState(state.url);
  const [loading, setLoading] = useState(true);
  // `blocked` = iframe hat nach `BLOCK_TIMEOUT_MS` noch nicht geladen,
  // sehr wahrscheinlich X-Frame-Options/CSP-Verbot. Heuristik, keine
  // Garantie — wir bieten auf jeden Fall Escape-Routen an.
  const [blocked, setBlocked] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // ESC schließt das Sheet — entspricht dem Verhalten unserer anderen
  // Glass-Dialogs (z. B. EventDialog, ExportSheet).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Block-Heuristik: wenn 4 s nach Load-Start kein `onLoad` kam, ist die
  // Seite mit hoher Wahrscheinlichkeit per X-Frame-Options gesperrt.
  useEffect(() => {
    setLoading(true);
    setBlocked(false);
    const t = window.setTimeout(() => {
      setBlocked(true);
      setLoading(false);
    }, BLOCK_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [currentUrl, reloadKey]);

  function handleIframeLoad() {
    setLoading(false);
    setBlocked(false);
  }

  const desktopTauri = isTauri();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={state.title}
      className="fixed inset-0 z-300 flex flex-col bg-black/60 backdrop-blur-sm"
      // Klick auf Overlay (aber nicht aufs Sheet) schließt — Standard
      // Glass-Sheet-Verhalten.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="glass-floating mx-auto mt-4 flex h-[calc(100svh-2rem)] w-[min(1200px,calc(100vw-1rem))] flex-col overflow-hidden rounded-3xl">
        {/* ── Toolbar ─────────────────────────────────── */}
        <header
          className="flex items-center gap-2 border-b px-3 py-2"
          style={{ borderColor: "rgba(255,255,255,0.08)" }}
        >
          <NavButton
            label="Zurück"
            disabled
            onClick={() => {
              /* cross-origin iframe history is nicht steuerbar */
            }}
          >
            <ArrowLeft className="size-4" />
          </NavButton>
          <NavButton label="Vor" disabled onClick={() => {}}>
            <ArrowRight className="size-4" />
          </NavButton>
          <NavButton label="Neu laden" onClick={() => setReloadKey((k) => k + 1)}>
            <RotateCw className="size-4" />
          </NavButton>

          <div className="mx-2 flex min-w-0 flex-1 items-center gap-2 rounded-xl bg-black/30 px-3 py-1.5 text-[12px] text-white/80">
            {loading && <Loader2 className="size-3.5 shrink-0 animate-spin opacity-70" />}
            <span className="truncate font-medium">{state.title}</span>
            <span className="truncate text-white/40">·</span>
            <span className="truncate text-white/50">{currentUrl}</span>
          </div>

          <NavButton
            label={desktopTauri ? "Im Tauri-Fenster" : "Im Browser"}
            onClick={() => openExternally(state.externalUrl)}
          >
            <ExternalLink className="size-4" />
          </NavButton>
          <NavButton label="Schließen" onClick={onClose} accent="danger">
            <X className="size-4" />
          </NavButton>
        </header>

        {/* ── Inhalt: Iframe + optionaler Block-Overlay ─── */}
        <div className="relative min-h-0 flex-1 bg-white">
          <iframe
            key={`${currentUrl}::${reloadKey}`}
            ref={iframeRef}
            src={currentUrl}
            title={state.title}
            // Sandbox so eng wie möglich, aber so offen wie nötig damit
            // Login-Flows, Forms, Pop-ups und Storage funktionieren.
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads"
            referrerPolicy="no-referrer-when-downgrade"
            allow="autoplay; clipboard-read; clipboard-write; fullscreen; geolocation; microphone; camera"
            onLoad={handleIframeLoad}
            className="size-full border-0"
          />

          {blocked && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 px-6 backdrop-blur-md">
              <div className="glass-floating max-w-md rounded-2xl p-6 text-center">
                <ShieldAlert
                  className="mx-auto mb-3 size-8"
                  style={{ color: "var(--color-tactical-red)" }}
                />
                <h3 className="mb-1 text-title-md font-semibold">
                  Diese Seite blockiert Einbettung
                </h3>
                <p className="mb-4 text-body-sm text-white/70">
                  Der Anbieter verbietet das Anzeigen innerhalb der App.
                  {desktopTauri
                    ? " Öffne sie stattdessen in einem eigenen Tauri-Fenster."
                    : " Öffne sie in einem neuen Browser-Tab."}
                </p>
                <button
                  type="button"
                  onClick={() => openExternally(state.externalUrl)}
                  className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold"
                  style={{
                    background: "var(--color-pitch-lime)",
                    color: "#1d3700",
                  }}
                >
                  <ExternalLink className="size-4" />
                  {desktopTauri ? "In Tauri-Fenster öffnen" : "Im Browser öffnen"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setBlocked(false);
                    setCurrentUrl(state.url);
                    setReloadKey((k) => k + 1);
                  }}
                  className="ml-2 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white/80 hover:text-white"
                  style={{ border: "1px solid rgba(255,255,255,0.18)" }}
                >
                  Trotzdem versuchen
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────── Helpers ─────────────────────────────── */

const BLOCK_TIMEOUT_MS = 4000;

function NavButton({
  children,
  label,
  onClick,
  disabled,
  accent,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  accent?: "danger";
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-8 place-items-center rounded-lg text-white/85 transition-colors hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent"
      style={accent === "danger" ? { color: "var(--color-tactical-red)" } : undefined}
    >
      {children}
    </button>
  );
}

/**
 * Spawnt ein eigenes Tauri-`WebviewWindow` (Desktop) bzw. fällt auf
 * `window.open` zurück (Web/PWA/Mobile-Tauri). So bleibt der User in
 * unserem App-Prozess statt im System-Default-Browser.
 */
async function openExternally(url: string): Promise<void> {
  if (isTauri()) {
    try {
      const mod = await import("@tauri-apps/api/webviewWindow");
      const label = `iab-${Date.now()}`;
      // Auf Tauri Mobile gibt es nur ein Fenster — `new` wirft dort und
      // wir fallen unten auf `window.open` zurück.
      new mod.WebviewWindow(label, {
        url,
        title: hostnameOf(url) ?? url,
        width: 1100,
        height: 800,
        resizable: true,
        focus: true,
      });
      return;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[in-app-browser] Tauri WebviewWindow failed, fallback", err);
    }
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

function normalizeUrl(input: string): string | null {
  try {
    const u = new URL(input);
    // Nur http(s) zulassen — verhindert javascript: / data: Injection.
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    return u.toString();
  } catch {
    // Fehlt das Schema, präfix https:// und nochmal probieren.
    try {
      const u = new URL(`https://${input}`);
      return u.toString();
    } catch {
      return null;
    }
  }
}

function hostnameOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}
